<template>
  <ValidationObserver ref="dialogObserver">
    <AppDialog
      class="reset-password-dialog"
      :title="stepTitle"
      :show="show"
      append-to-body
      @close="close"
    >
      <template v-if="creating">
        <form
          id="reset-password-dialog"
          autocomplete="off"
          @submit.prevent="onFormSubmit"
        >
          <h3 class="mt-0">{{ user.name }}</h3>

          <div class="d-inline-flex break-word align-items-center mb-10">
            <i class="hi hi-16 hi-mail text-lighter mr-10" />
            {{ user.email }}
          </div>

          <label class="control-label mt-20">Password</label>

          <div class="row">
            <div class="col-sm-12 form-check radio">
              <div
                class="d-inline-flex align-items-center"
                @click="onSelectAutoPassword"
              >
                <input
                  class="form-check-input mt-0"
                  type="radio"
                  :checked="!createPassword"
                />
                <label
                  for="auto_generate_password"
                  class="password optional form-check-label"
                >
                  Automatically generate password
                </label>
              </div>

              <p class="text-lighter ml-20 mt-5">You’ll be able to view and copy the password in the next step</p>
            </div>
          </div>

          <div class="row">
            <div class="col-sm-12 form-check radio">
              <div
                class="d-inline-flex align-items-center"
                @click="onSelectCreatePassword"
              >
                <input
                  class="form-check-input mt-0"
                  type="radio"
                  :checked="createPassword"
                />
                <label
                  for="create_password"
                  class="password optional form-check-label"
                >
                  Create password
                </label>
              </div>

              <ValidationProvider
                v-slot="{ errors }"
                :rules="requiredField"
                mode="lazy"
                name="new_password"
              >
                <div
                  v-if="createPassword"
                  class="form-group password ml-20 mt-10"
                  :class="{ 'has-error': errors[0] }"
                >
                  <input
                    v-model="newPassword"
                    class="password optional form-control password optional"
                    :type="passwordType"
                  />
                  <i
                    class="hi hi-eye absolute"
                    :class="{ 'text-lighter': !showPassword }"
                    @click="togglePasswordVisibility"
                  />
                  <AppDialogError :errors="errors" />
                </div>

                <!-- Currently requiring a user to change their password is not available -->
                <!--              <AppCheckbox-->
                <!--                :value="requireChangePassword"-->
                <!--                class="mt-10"-->
                <!--              >-->
                <!--                <span class="is-regular text-lighter">-->
                <!--                  Require them to change their password on first sign in-->
                <!--                </span>-->
                <!--              </AppCheckbox>-->
              </ValidationProvider>
            </div>
          </div>

          <div class="footer">
            <div class="d-flex mt-40">
              <div class="ml-auto">
                <AppButton
                  class="mr-15"
                  type="default"
                  @click="close"
                >
                  Cancel
                </AppButton>

                <button
                  class="btn btn-primary"
                  :disabled="submitting"
                  type="submit"
                >
                  {{ resetButtonLabel }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </template>

      <template v-else>
        <div class="dialog-warning blue d-flex align-items-center">
          <i class="hi hi-info-filled hi-16 pt-0"></i>
          <div>Don’t forget to copy password and share with the new member</div>
        </div>
        <div class="mt-20">
          <img
            :src="passwordCreatedImage"
            class="d-block mx-auto"
            alt="Password created"
            height="151"
          />
        </div>
        <div class="text-lighter text-center">
          <button
            class="btn btn-primary"
            :class="{ active: copyButtonActive }"
            @click="copyPassword"
          >
            {{ copyPasswordButtonLabel }}
          </button>
        </div>
        <div class="text-lighter mt-20 text-center">
          <p>
            <span class="mr-5">Email:</span>
            <span class="confirmed-email">{{ user.email }}</span>
          </p>
        </div>
        <div class="text-lighter text-center">
          <p>
            <span class="mr-5"> Password: </span>
            <span v-if="showNewPassword">
              {{ newPassword }}
            </span>
            <span v-else> ********* </span>
            <i
              class="hi hi-eye clickable ml-10"
              :class="{ 'text-blue': showNewPassword }"
              @click="toggleNewPassword"
            />
          </p>
        </div>

        <div class="footer">
          <div class="d-flex">
            <div class="ml-auto">
              <AppButton
                class="mr-15"
                type="outline-primary"
                @click="close"
              >
                Done
              </AppButton>
            </div>
          </div>
        </div>
      </template>
    </AppDialog>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { IMAGE_PASSWORD_CREATED } from '~/rails_assets';
import { extend } from '~/utils/vee_validate_compat';
import { min } from '@vee-validate/rules';
import { handleFormErrors } from '~/utils/form_utils';

extend('min', {
  ...min,
  message: 'is too short (minimum is six characters)',
});

export default {
  name: 'MemberResetPasswordDialog',
  props: {
    show: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      creating: true,
      createPassword: false,
      requireChange: true,
      showPassword: false,
      newPassword: null,
      passwordCreatedImage: IMAGE_PASSWORD_CREATED,
      showNewPassword: false,
      submitting: false,
      copyPasswordButtonLabel: 'COPY PASSWORD',
      copyButtonActive: false,
    };
  },
  computed: {
    ...mapState({
      errors: 'errors',
      membership: 'membership',
      user: (state) => state.membership.user,
    }),
    ...mapGetters(['organizationId']),
    stepTitle() {
      return this.creating ? 'Reset password' : 'New password has been created';
    },
    requireChangePassword() {
      return this.createPassword && this.requireChange;
    },
    passwordType() {
      return this.showPassword ? 'text' : 'password';
    },
    resetButtonLabel() {
      return this.submitting ? 'Resetting...' : 'Reset password';
    },
    requiredField() {
      return this.createPassword ? 'required|min:6' : '';
    },
  },
  methods: {
    ...mapActions(['resetPassword', 'setErrors']),
    close() {
      this.$emit('close');
    },
    onSelectAutoPassword() {
      this.createPassword = false;
    },
    onSelectCreatePassword() {
      this.createPassword = true;
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    async onFormSubmit() {
      const validated = await this.$refs.dialogObserver.validate();

      if (validated) {
        this.submitting = true;
        this.resetPassword({
          organizationId: this.organizationId,
          userId: this.user.id,
          password: this.newPassword,
        }).then(this.completeRequest);
      }
    },
    completeRequest(response) {
      this.submitting = false;

      if (response && response.password) {
        this.creating = false;
        this.newPassword = response.password;
      } else {
        this.creating = true;
        this.setErrors(response && response.errors);
        handleFormErrors(this.$refs.dialogObserver, this.errors, this.$appMessage);
      }
    },
    toggleNewPassword() {
      this.showNewPassword = !this.showNewPassword;
    },
    copyPassword() {
      this.copyPasswordButtonLabel = 'Password copied!';
      this.copyButtonActive = true;

      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.newPassword);
      }

      setTimeout(() => {
        this.copyPasswordButtonLabel = 'COPY PASSWORD';
        this.copyButtonActive = false;
      }, 2000);
    },
  },
};
</script>
